/*
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { Sentry } from "services/sentry"

// AIDEV-NOTE: sessionStorage access can throw SecurityError (DOMException code 18) rather than
// simply being unavailable - observed in Firefox with strict tracking protection / storage
// partitioning, and known to also occur in Safari private browsing. Every call site must be
// wrapped, not just guarded for existence. See GitHub issue #165 / Sentry JAVASCRIPT-REACT-2M.
// AIDEV-NOTE: "storage_access_error" covers the sessionStorage API itself throwing (access
// denial, e.g. SecurityError/quota). It is intentionally distinct from Calculator.tsx's
// "storage_parsing_error", which fires when sessionStorage was readable but returned malformed
// JSON - a data-integrity failure downstream of a successful read, not an access failure.
const reportStorageError = (error: unknown, operation: "getItem" | "setItem" | "removeItem") => {
  Sentry.captureException(error, {
    tags: { errorType: "storage_access_error", component: "safeSessionStorage", operation },
  })
}

/**
 * Reads a sessionStorage key, returning `null` if storage is unavailable or access throws
 * (rather than letting the exception propagate and crash the caller).
 */
export const safeSessionStorageGetItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch (error) {
    reportStorageError(error, "getItem")
    return null
  }
}

/**
 * Writes a sessionStorage key, silently no-op'ing (after reporting) if storage is unavailable
 * or access throws.
 */
export const safeSessionStorageSetItem = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value)
  } catch (error) {
    reportStorageError(error, "setItem")
  }
}

/**
 * Removes a sessionStorage key, silently no-op'ing (after reporting) if storage is unavailable
 * or access throws.
 */
export const safeSessionStorageRemoveItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    reportStorageError(error, "removeItem")
  }
}
